import React from 'react'

/**
 * OptimizedImage component for responsive images with WebP support
 * Supports lazy loading, fetchpriority, and responsive srcset
 */
const OptimizedImage = ({
  src,
  alt,
  className = '',
  loading = 'lazy',
  fetchPriority = null,
  sizes = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw',
  width = null,
  height = null,
  onError = null,
  ...props
}) => {
  // Generate WebP version URL if from Supabase or external source
  const getWebPSrc = (originalSrc) => {
    if (!originalSrc) return null
    
    // If it's a Supabase storage URL, we can't convert on the fly
    // But we can add format parameter if Supabase supports it
    if (originalSrc.includes('supabase.co/storage')) {
      // Supabase Storage doesn't auto-convert, so return original
      // In production, consider using a CDN/image optimization service
      return originalSrc
    }
    
    // For Unsplash images, we can request WebP format
    if (originalSrc.includes('unsplash.com')) {
      return originalSrc.replace(/&(w|h|fit)=[^&]*/g, '') + '&fm=webp&auto=format'
    }
    
    // For other URLs, return original (would need backend conversion)
    return originalSrc
  }

  // Generate responsive srcset for different sizes
  const generateSrcSet = (baseSrc) => {
    if (!baseSrc) return null
    
    // For Unsplash, generate multiple sizes with WebP format
    if (baseSrc.includes('unsplash.com')) {
      const baseUrl = baseSrc.split('?')[0]
      const params = new URLSearchParams(baseSrc.split('?')[1] || '')
      const fit = params.get('fit') || 'crop'
      const h = params.get('h') || '900'
      const w = params.get('w') || '1600'
      
      // Generate responsive sizes based on original dimensions
      const sizes = []
      if (parseInt(w) >= 400) sizes.push(`${baseUrl}?w=400&h=${Math.round(400 * parseInt(h) / parseInt(w))}&fit=${fit}&fm=webp&auto=format&q=80 400w`)
      if (parseInt(w) >= 800) sizes.push(`${baseUrl}?w=800&h=${Math.round(800 * parseInt(h) / parseInt(w))}&fit=${fit}&fm=webp&auto=format&q=80 800w`)
      if (parseInt(w) >= 1200) sizes.push(`${baseUrl}?w=1200&h=${Math.round(1200 * parseInt(h) / parseInt(w))}&fit=${fit}&fm=webp&auto=format&q=80 1200w`)
      sizes.push(`${baseUrl}?w=${w}&h=${h}&fit=${fit}&fm=webp&auto=format&q=80 ${w}w`)
      
      return sizes.join(', ')
    }
    
    // For other images, return null (use single src)
    return null
  }

  const webpSrc = getWebPSrc(src)
  const srcSet = generateSrcSet(src)

  return (
    <picture>
      {/* WebP source with srcset if available */}
      {webpSrc && srcSet && (
        <source
          srcSet={srcSet}
          sizes={sizes}
          type="image/webp"
        />
      )}
      {/* Fallback img tag */}
      <img
        src={src}
        srcSet={srcSet || undefined}
        sizes={sizes}
        alt={alt}
        className={className}
        loading={loading}
        fetchpriority={fetchPriority}
        width={width}
        height={height}
        decoding={props.decoding || (loading === 'eager' ? 'sync' : 'async')}
        onError={onError}
        {...props}
      />
    </picture>
  )
}

export default OptimizedImage
